import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Users } from "lucide-react";

import { useAuth } from "@/components/AuthProvider";
import { HubUserAvatar } from "@/components/HubUserAvatar";
import {
  fetchHubPresenceAll,
  hubPresenceAllQueryKey,
  type HubPresenceUserGroup,
} from "@/lib/hubPresenceApi";
import { useHubPagePresenceStream } from "@/lib/useHubPagePresenceStream";
import { cn } from "@/lib/utils";
import {
  computeHorizontalMenuPanel,
  FLOATING_POPOVER_Z_INDEX,
  type AnchoredMenuPanelBox,
} from "@/lib/viewportAnchoredPopover";

function displayNameFor(user: HubPresenceUserGroup): string {
  const name = user.displayName?.trim();
  return name != null && name !== "" ? name : user.email;
}

function isSafeOpenPath(path: string): boolean {
  if (!path.startsWith("/") || path.startsWith("//")) {
    return false;
  }
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(path)) {
    return false;
  }
  if (/https?:/i.test(path) || path.includes("\\")) {
    return false;
  }
  return true;
}

type GlobalPresencePanelProps = {
  /** Controlled open state (used when embedded in UserMenu). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Anchor element for panel positioning when `showTrigger` is false. */
  anchorRef?: RefObject<HTMLElement | null>;
  /** When false, only the floating panel is rendered (no toolbar button). */
  showTrigger?: boolean;
};

/** Global People control: count when closed; grouped live pages while open. */
export function GlobalPresencePanel(props: GlobalPresencePanelProps = {}): JSX.Element | null {
  const { showTrigger = true, anchorRef: externalAnchorRef } = props;
  const { session } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = props.open !== undefined;
  const open = isControlled ? Boolean(props.open) : internalOpen;
  const setOpen = useCallback(
    (value: boolean | ((previous: boolean) => boolean)) => {
      const currentOpen = isControlled ? Boolean(props.open) : internalOpen;
      const next = typeof value === "function" ? value(currentOpen) : value;
      if (isControlled) {
        props.onOpenChange?.(next);
      } else {
        setInternalOpen(next);
      }
    },
    [isControlled, internalOpen, props.onOpenChange, props.open],
  );
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const positionRef = showTrigger ? triggerRef : externalAnchorRef;
  const panelRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const [panelBox, setPanelBox] = useState<AnchoredMenuPanelBox | null>(null);
  const refreshTimerRef = useRef<number | undefined>(undefined);

  const userId = session?.user?.id ?? null;
  const enabled = userId != null && userId !== "";

  const refreshAll = useCallback((): void => {
    if (refreshTimerRef.current != null) {
      return;
    }
    refreshTimerRef.current = window.setTimeout(() => {
      refreshTimerRef.current = undefined;
      if (userId != null) {
        void queryClient.invalidateQueries({ queryKey: hubPresenceAllQueryKey(userId) });
      }
    }, 500);
  }, [queryClient, userId]);

  useEffect(
    () => () => {
      if (refreshTimerRef.current != null) {
        window.clearTimeout(refreshTimerRef.current);
      }
    },
    [],
  );

  useHubPagePresenceStream({
    scope: "global",
    enabled: enabled && open,
    connectionKey: userId,
    onPresenceChange: refreshAll,
  });

  const allQuery = useQuery({
    queryKey: hubPresenceAllQueryKey(userId ?? "unauthenticated"),
    queryFn: fetchHubPresenceAll,
    enabled: enabled && open,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  const users = allQuery.data?.users ?? [];
  const liveUserCount = users.length;

  const reposition = useCallback((): void => {
    if (!open || positionRef?.current == null) {
      setPanelBox(null);
      return;
    }
    setPanelBox(
      computeHorizontalMenuPanel({
        anchorRect: positionRef.current.getBoundingClientRect(),
        menuWidth: Math.round(22 * 16),
        horizontalAlign: "end",
        gap: 4,
        maxHeightCap: Math.min(520, Math.floor(window.innerHeight * 0.85)),
      }),
    );
  }, [open, positionRef]);

  useLayoutEffect(() => {
    if (!open) {
      setPanelBox(null);
      return;
    }
    reposition();
    const onLayout = (): void => {
      reposition();
    };
    window.addEventListener("resize", onLayout);
    window.addEventListener("scroll", onLayout, true);
    window.visualViewport?.addEventListener("resize", onLayout);
    window.visualViewport?.addEventListener("scroll", onLayout);
    return () => {
      window.removeEventListener("resize", onLayout);
      window.removeEventListener("scroll", onLayout, true);
      window.visualViewport?.removeEventListener("resize", onLayout);
      window.visualViewport?.removeEventListener("scroll", onLayout);
    };
  }, [open, reposition, users.length]);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (event: MouseEvent): void => {
      if (
        rootRef.current?.contains(event.target as Node) ||
        panelRef.current?.contains(event.target as Node)
      ) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        setOpen(false);
        if (showTrigger) {
          triggerRef.current?.focus();
        } else {
          externalAnchorRef?.current?.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, setOpen, showTrigger, externalAnchorRef]);

  const countLabel = useMemo(() => {
    if (!open) return "People";
    if (liveUserCount <= 0) return "People";
    return `People (${String(liveUserCount)})`;
  }, [liveUserCount, open]);

  if (!enabled) {
    return null;
  }

  return (
    <div ref={rootRef} className={cn(showTrigger && "relative flex flex-shrink-0")}>
      {showTrigger ? (
        <button
          ref={triggerRef}
          type="button"
          className={cn(
            "inline-flex h-9 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 text-sm font-medium text-slate-700",
            "transition-colors hover:border-slate-300 hover:bg-slate-50",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2",
            open && "border-slate-300 bg-slate-50",
          )}
          aria-expanded={open}
          aria-haspopup="dialog"
          aria-controls={listId}
          aria-label={
            open && liveUserCount > 0
              ? `People online, ${String(liveUserCount)} viewing Hub pages`
              : "People online"
          }
          onClick={() => setOpen((value) => !value)}
        >
          <Users className="h-4 w-4 text-slate-500" aria-hidden />
          <span>{countLabel}</span>
        </button>
      ) : null}

      {open && panelBox != null
        ? createPortal(
            <div
              ref={panelRef}
              id={listId}
              role="dialog"
              aria-label="People viewing Hub pages"
              className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg"
              style={{
                position: "fixed",
                top: panelBox.top,
                left: panelBox.left,
                width: panelBox.width,
                maxHeight: panelBox.maxHeight,
                zIndex: FLOATING_POPOVER_Z_INDEX,
                ...(panelBox.transform ? { transform: panelBox.transform } : {}),
              }}
            >
              <div className="border-b border-slate-100 px-3 py-2">
                <p className="text-sm font-semibold text-slate-900">People</p>
                <p className="text-xs text-slate-500">Who is viewing Hub pages right now</p>
              </div>
              <div className="overflow-y-auto" style={{ maxHeight: Math.max(120, panelBox.maxHeight - 56) }}>
                {allQuery.isLoading ? (
                  <p className="px-3 py-4 text-sm text-slate-500">Loading…</p>
                ) : allQuery.isError ? (
                  <div className="space-y-2 px-3 py-4">
                    <p className="text-sm text-slate-600">Could not load people.</p>
                    <button
                      type="button"
                      className="text-sm font-medium text-brand-700 hover:underline"
                      onClick={() => void allQuery.refetch()}
                    >
                      Retry
                    </button>
                  </div>
                ) : users.length === 0 ? (
                  <p className="px-3 py-4 text-sm text-slate-500">No one else is viewing Hub pages.</p>
                ) : (
                  <ul className="divide-y divide-slate-100 py-1">
                    {users.map((user) => {
                      const name = displayNameFor(user);
                      const idle = user.presenceState === "idle";
                      return (
                        <li key={user.userId} className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <span className={cn(idle && "opacity-60 grayscale")}>
                              <HubUserAvatar avatarUrl={user.avatarUrl} label={name} size="sm" />
                            </span>
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-medium text-slate-900">{name}</p>
                              <p className="text-[11px] uppercase tracking-wide text-slate-500">
                                {user.presenceState === "active" ? "Active" : "Idle"}
                              </p>
                            </div>
                          </div>
                          <ul className="mt-1.5 space-y-1 pl-10">
                            {user.pages.map((page) => {
                              const canOpen = isSafeOpenPath(page.navigablePath);
                              return (
                                <li
                                  key={`${user.userId}:${page.routeKey}`}
                                  className="flex items-start justify-between gap-2"
                                >
                                  <div className="min-w-0">
                                    <p className="truncate text-xs text-slate-700">{page.pageLabel}</p>
                                    <p className="text-[11px] text-slate-500">
                                      {page.presenceState === "active" ? "Active" : "Idle"}
                                      {page.tabCount > 1 ? ` · ${String(page.tabCount)} tabs` : ""}
                                      {!page.isVisible ? " · Hidden tab" : ""}
                                    </p>
                                  </div>
                                  {canOpen ? (
                                    <button
                                      type="button"
                                      className="shrink-0 rounded border border-slate-200 bg-white px-2 py-0.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                                      onClick={() => {
                                        setOpen(false);
                                        navigate(page.navigablePath);
                                      }}
                                    >
                                      Open
                                    </button>
                                  ) : null}
                                </li>
                              );
                            })}
                          </ul>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
